import React from 'react'
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
} from '@mui/material'
import {
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    ClearAll as ClearAllIcon,
    Refresh as RefreshIcon,
    Computer as ComputerIcon,
    Phone as PhoneIcon,
    Warning as WarningIcon,
} from '@mui/icons-material'
import { styled } from '@mui/material/styles'

const StyledModal = styled(Modal)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
})

const ModalContent = styled(Box)(({ theme }) => ({
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    outline: 'none',
    padding: theme.spacing(4),
    maxWidth: '650px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '4px',
    },
}))

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    '&:before': {
        display: 'none',
    },
    '&.Mui-expanded': {
        margin: `${theme.spacing(1)} 0`,
    },
}))

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    '&.Mui-expanded': {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    '& .MuiAccordionSummary-content': {
        alignItems: 'center',
    },
}))

const KeyboardShortcut = styled(Chip)(({ theme }) => ({
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    fontSize: '0.75rem',
    height: '24px',
    margin: theme.spacing(0, 0.5),
}))

interface VersionMismatchHelpModalProps {
    open: boolean
    onClose: () => void
}

const VersionMismatchHelpModal: React.FC<VersionMismatchHelpModalProps> = ({ open, onClose }) => {
    const handleClearCache = () => {
        if ('caches' in window) {
            caches.keys().then(names => {
                names.forEach(name => {
                    caches.delete(name)
                })
            }).finally(() => {
                window.location.reload()
            })
        } else {
            (window as Window).location.href = (window as Window).location.href.split('?')[0] + '?nocache=' + new Date().getTime()
        }
    }

    return (
        <StyledModal
            open={open}
            onClose={onClose}
            BackdropProps={{
                style: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
            }}
        >
            <ModalContent>
                <IconButton
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'grey.500',
                    }}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>

                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{ fontWeight: 600, mb: 1 }}
                    >
                        Version Update Help
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Follow these steps to resolve version mismatch issues
                    </Typography>
                </Box>

                <Box>
                    <StyledAccordion defaultExpanded>
                        <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <ClearAllIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Clear Browser Cache (Recommended)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Most effective solution for version issues
                                </Typography>
                            </Box>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Perform a "Hard Reload" to clear cached files and get the latest version:
                                </Typography>
                                
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                        <ComputerIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Desktop/Laptop:
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemText 
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <strong>Windows/Linux:</strong>
                                                        <KeyboardShortcut label="Ctrl" />
                                                        +
                                                        <KeyboardShortcut label="Shift" />
                                                        +
                                                        <KeyboardShortcut label="R" />
                                                        <span style={{ marginLeft: '8px' }}>or</span>
                                                        <KeyboardShortcut label="Ctrl" />
                                                        +
                                                        <KeyboardShortcut label="F5" />
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText 
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <strong>Mac:</strong>
                                                        <KeyboardShortcut label="Cmd" />
                                                        +
                                                        <KeyboardShortcut label="Shift" />
                                                        +
                                                        <KeyboardShortcut label="R" />
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    </List>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                        <PhoneIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Mobile Device:
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemText primary="Pull down on the page to refresh, or close and reopen your browser app" />
                                        </ListItem>
                                    </List>
                                </Box>

                                <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, mb: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                        Quick Auto-Clear (Click below):
                                    </Typography>
                                    <button
                                        onClick={handleClearCache}
                                        style={{
                                            backgroundColor: '#1976d2',
                                            color: 'white',
                                            border: 'none',
                                            padding: '8px 16px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                        }}
                                    >
                                        Clear Cache & Reload Now
                                    </button>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </StyledAccordion>

                    {/* Manual Browser Cache Clear */}
                    <StyledAccordion>
                        <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <WarningIcon sx={{ mr: 2, color: 'warning.main' }} />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Manual Cache Clear
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    If keyboard shortcuts don't work
                                </Typography>
                            </Box>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Clear cache through browser settings:
                                </Typography>
                                
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>1.</ListItemIcon>
                                        <ListItemText primary="Open your browser's Developer Tools (F12)" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>2.</ListItemIcon>
                                        <ListItemText primary="Right-click on the refresh button in your browser" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>3.</ListItemIcon>
                                        <ListItemText primary='Select "Empty Cache and Hard Reload"' />
                                    </ListItem>
                                </List>

                                <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
                                    Alternative - Browser Settings:
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>•</ListItemIcon>
                                        <ListItemText primary="Go to browser Settings → Privacy/Security → Clear browsing data" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>•</ListItemIcon>
                                        <ListItemText primary="Select 'Cached images and files' and clear" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>•</ListItemIcon>
                                        <ListItemText primary="Refresh the page" />
                                    </ListItem>
                                </List>
                            </Box>
                        </AccordionDetails>
                    </StyledAccordion>

                    {/* Disable Cache (For Developers) */}
                    <StyledAccordion>
                        <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <ComputerIcon sx={{ mr: 2, color: 'success.main' }} />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Disable Cache (Developers)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Prevent future cache issues during development
                                </Typography>
                            </Box>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    For developers working on this application:
                                </Typography>
                                
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>1.</ListItemIcon>
                                        <ListItemText primary="Open Developer Tools (F12)" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>2.</ListItemIcon>
                                        <ListItemText primary="Go to the Network tab" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>3.</ListItemIcon>
                                        <ListItemText primary='Check "Disable cache" while DevTools is open' />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>4.</ListItemIcon>
                                        <ListItemText primary="Keep DevTools open while working" />
                                    </ListItem>
                                </List>
                            </Box>
                        </AccordionDetails>
                    </StyledAccordion>

                    {/* Still Having Issues */}
                    <StyledAccordion>
                        <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <RefreshIcon sx={{ mr: 2, color: 'error.main' }} />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Still Having Issues?
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Additional troubleshooting steps
                                </Typography>
                            </Box>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>•</ListItemIcon>
                                        <ListItemText 
                                            primary="Try using an incognito/private browser window"
                                            secondary="This bypasses all cached data"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>•</ListItemIcon>
                                        <ListItemText 
                                            primary="Try a different browser"
                                            secondary="Chrome, Firefox, Safari, or Edge"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>•</ListItemIcon>
                                        <ListItemText 
                                            primary="Check your internet connection"
                                            secondary="Ensure you can access other websites"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>•</ListItemIcon>
                                        <ListItemText 
                                            primary="Contact IT support"
                                            secondary="If none of the above solutions work"
                                        />
                                    </ListItem>
                                </List>
                            </Box>
                        </AccordionDetails>
                    </StyledAccordion>
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                    >
                        If you continue to experience issues, please contact the development team.
                    </Typography>
                </Box>
            </ModalContent>
        </StyledModal>
    )
}

export default VersionMismatchHelpModal